/* eslint-disable max-lines-per-function */
import { createSelector } from '@ngrx/store';

import type { MakeRef } from '../types';
import { createActionsWithReducer, errorContextProxyHandler } from '../utils';
import type { SingleEntityActions } from './actions';
import { createSingleEntityAdapter } from './adapter';
import type {
  NotNull,
  SingleEntityState,
  SingleEntityStoreCustomActions,
  SingleEntityStoreOptions,
} from './types';

export function createSingleEntityStore<Entity extends object | null>() {
  return <StoreOptions extends SingleEntityStoreOptions<Entity>>(
    namespace: string,
    options?: StoreOptions,
  ) => {
    const ns = `entity/${namespace}`;

    const baseSelector = createSelector(
      (root: any) => <SingleEntityState<Entity>>root[ns],
      state => state,
    );

    const makeRef: MakeRef<NotNull<Entity>> = (mapFrom, mapTo, source) => ({
      mapFrom,
      mapTo,
      source,
    });
    const {
      adapter,
      initialState: defaultInitialState,
      selectors,
      defaultActionsConfig,
    } = createSingleEntityAdapter<Entity>(baseSelector, ns, options?.references?.(makeRef) || []);
    const mergedActions = <
      SingleEntityActions<Entity> & SingleEntityStoreCustomActions<StoreOptions>
    >{
      ...defaultActionsConfig,
      ...(<SingleEntityStoreCustomActions<StoreOptions>>(options?.customActions?.(adapter) || {})),
    };

    const selectorsWithErrorContext = <typeof selectors>(
      new Proxy(selectors, errorContextProxyHandler([ns]))
    );
    const { actions, reducer } = createActionsWithReducer(
      namespace,
      options?.initialState || defaultInitialState,
      mergedActions,
    );
    return {
      namespace: ns,
      selectors: selectorsWithErrorContext,
      actions,
      reducer,
    };
  };
}
